/**
 * Gate-1 API base URL validation (https:// or http://).
 * Returns { ok, value, error } where value is always trimmed.
 */

const MAX_LENGTH = 255;

function isValidIpv4(hostname) {
  const parts = String(hostname || "").split(".");
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;
    const n = Number(part);
    return n >= 0 && n <= 255;
  });
}

function looksLikeIpv4(hostname) {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(String(hostname || ""));
}

/** Domain labels: letters/digits/hyphen, not starting/ending with hyphen; requires a dot. */
function isValidDomainHostname(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (!host || host.length > 253) return false;
  if (!host.includes(".")) return false;
  if (host.includes("..") || host.startsWith(".") || host.endsWith(".")) return false;
  return host.split(".").every((label) => {
    if (!label || label.length > 63) return false;
    if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label)) return false;
    return true;
  });
}

/**
 * @param {unknown} raw
 * @returns {{ ok: boolean, value: string, error: string }}
 */
export function validateApiBaseUrl(raw) {
  const value = String(raw ?? "").trim();

  if (!value) {
    return { ok: true, value: "", error: "" };
  }

  if (value.length > MAX_LENGTH) {
    return {
      ok: false,
      value,
      error: "API base URL must be 255 characters or fewer",
    };
  }

  if (/\s/.test(value)) {
    return {
      ok: false,
      value,
      error: "API base URL must not contain spaces",
    };
  }

  if (/^ftp:\/\//i.test(value)) {
    return {
      ok: false,
      value,
      error: "API base URL must start with https:// or http://",
    };
  }

  if (!/^https?:\/\//i.test(value)) {
    return {
      ok: false,
      value,
      error: "API base URL must start with https:// or http://",
    };
  }

  if (value.includes("?")) {
    return {
      ok: false,
      value,
      error: "Query parameters are not allowed in the API base URL",
    };
  }

  let url;
  try {
    url = new URL(value);
  } catch {
    return { ok: false, value, error: "Enter a valid API base URL" };
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return {
      ok: false,
      value,
      error: "API base URL must start with https:// or http://",
    };
  }

  if (url.username || url.password) {
    return {
      ok: false,
      value,
      error: "Username and password are not allowed in the API base URL",
    };
  }

  const hostname = url.hostname;
  if (!hostname) {
    return { ok: false, value, error: "Hostname is required" };
  }

  if (/\s/.test(hostname) || /\s/.test(url.pathname || "")) {
    return {
      ok: false,
      value,
      error: "API base URL must not contain spaces",
    };
  }

  if (url.port) {
    const portNum = Number(url.port);
    if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) {
      return {
        ok: false,
        value,
        error: "Port must be between 1 and 65535",
      };
    }
  }

  if (hostname.toLowerCase() === "localhost") {
    return { ok: true, value, error: "" };
  }

  if (looksLikeIpv4(hostname)) {
    if (!isValidIpv4(hostname)) {
      return { ok: false, value, error: "Enter a valid hostname" };
    }
    return { ok: true, value, error: "" };
  }

  // Short names without a dot (e.g. https://api) are rejected; localhost handled above.
  if (!hostname.includes(".")) {
    return { ok: false, value, error: "Enter a valid hostname" };
  }

  if (!isValidDomainHostname(hostname)) {
    return { ok: false, value, error: "Enter a valid hostname" };
  }

  return { ok: true, value, error: "" };
}

const MONITORING_URL_PROTOCOL_ERROR = "Monitoring URL must start with https:// or http://";
const DEPLOYMENT_PLAN_URL_PROTOCOL_ERROR = "Deployment plan URL must start with https:// or http://";

/**
 * Optional http(s) URL. Empty is allowed. Callers keep their own error text and storage key.
 * @param {unknown} raw
 * @param {{ protocolError: string, hostnameError: string }} messages
 * @returns {{ ok: boolean, value: string, error: string }}
 */
function validateOptionalHttpUrl(raw, messages) {
  const value = String(raw ?? "").trim();
  if (!value) return { ok: true, value: "", error: "" };

  let url;
  try {
    url = new URL(value);
  } catch {
    return { ok: false, value, error: messages.protocolError };
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return { ok: false, value, error: messages.protocolError };
  }

  if (!url.hostname) {
    return { ok: false, value, error: messages.hostnameError };
  }

  return { ok: true, value, error: "" };
}

/**
 * Monitoring signals URL. Empty is allowed. A value must be a parseable http(s) URL.
 * @param {unknown} raw
 * @returns {{ ok: boolean, value: string, error: string }}
 */
export function validateMonitoringSignalsUrl(raw) {
  return validateOptionalHttpUrl(raw, {
    protocolError: MONITORING_URL_PROTOCOL_ERROR,
    hostnameError: "Enter a valid monitoring URL",
  });
}

/**
 * Deployment plan URL. Empty is allowed. A value must be a parseable http(s) URL.
 * @param {unknown} raw
 * @returns {{ ok: boolean, value: string, error: string }}
 */
export function validateDeploymentPlanUrl(raw) {
  return validateOptionalHttpUrl(raw, {
    protocolError: DEPLOYMENT_PLAN_URL_PROTOCOL_ERROR,
    hostnameError: "Enter a valid deployment plan URL",
  });
}

/** Validated http(s) href, or "" when the value is empty or not a safe link. */
export function deploymentPlanHref(raw) {
  const check = validateDeploymentPlanUrl(raw);
  return check.ok ? check.value : "";
}

/**
 * URL to show when Gate 1 reopens. The project record wins; a session snapshot
 * supplies the value only when the project has not stored one.
 */
export function resolveMonitoringSignalsUrl(projectGate0, sessionGate0) {
  const projectValue = String(projectGate0?.monitoring_signals_url ?? "").trim();
  if (projectValue) return projectValue;
  if (!sessionGate0 || typeof sessionGate0 !== "object") return "";
  return String(sessionGate0.monitoring_signals_url ?? "").trim();
}

/** Validate a monitoring URL and build the gate0_data object to persist. Empty is allowed. */
export function buildMonitoringSignalsSave(gate0 = {}, raw) {
  const check = validateMonitoringSignalsUrl(raw);
  if (!check.ok) return { ok: false, message: check.error, gate0 };
  return {
    ok: true,
    value: check.value,
    gate0: applyMonitoringSignalsUrl(gate0, check.value),
    message: "Monitoring URL saved.",
  };
}

/** Keep a valid monitoring URL on gate0_data. An invalid draft does not replace a saved value. */
export function applyMonitoringSignalsUrl(gate0 = {}, raw) {
  const check = validateMonitoringSignalsUrl(raw);
  if (!check.ok) return gate0;
  const next = { ...gate0 };
  if (check.value) next.monitoring_signals_url = check.value;
  else delete next.monitoring_signals_url;
  return next;
}

/**
 * URL to show when Gate 1 reopens. The project record wins; a session snapshot
 * supplies the value only when the project has not stored one.
 */
export function resolveDeploymentPlanUrl(projectGate0, sessionGate0) {
  const projectValue = String(projectGate0?.deployment_plan_url ?? "").trim();
  if (projectValue) return projectValue;
  if (!sessionGate0 || typeof sessionGate0 !== "object") return "";
  return String(sessionGate0.deployment_plan_url ?? "").trim();
}

/** Validate a deployment plan URL and build the gate0_data object to persist. Empty is allowed. */
export function buildDeploymentPlanSave(gate0 = {}, raw) {
  const check = validateDeploymentPlanUrl(raw);
  if (!check.ok) return { ok: false, message: check.error, gate0 };
  return {
    ok: true,
    value: check.value,
    gate0: applyDeploymentPlanUrl(gate0, check.value),
    message: "Deployment plan link saved.",
  };
}

/** Keep a valid deployment plan URL on gate0_data. An invalid draft does not replace a saved value. */
export function applyDeploymentPlanUrl(gate0 = {}, raw) {
  const check = validateDeploymentPlanUrl(raw);
  if (!check.ok) return gate0;
  const next = { ...gate0 };
  if (check.value) next.deployment_plan_url = check.value;
  else delete next.deployment_plan_url;
  return next;
}

/** Apply both optional links. Each key is updated on its own, so one draft cannot clear the other. */
export function applyReadinessLinkUrls(gate0 = {}, { monitoringUrl, deploymentPlanUrl } = {}) {
  return applyDeploymentPlanUrl(applyMonitoringSignalsUrl(gate0, monitoringUrl), deploymentPlanUrl);
}
